/**
 * Single-player HUD and complete menu journey.
 *
 * Slot 0 is always the human. Slot 1 is always the computer (or the non-attacking training
 * dummy). The interface deliberately has no local-versus route or second-human language.
 */

import { DisposalBag, System, clamp } from '../core/index.js';
import { findFighter, loadRosterView } from './data/roster-view.js';
import {
  AI_DIFFICULTY_OPTIONS, computerOpponent, difficultyIndex,
} from './flow.js';

const RESULT_DELAY_TICKS = 180;
const MENU_SCREENS = new Set(['title', 'select', 'difficulty', 'pause', 'results']);

export class UiSystem extends System {
  static id = 'ui';
  static deps = ['combat-sim'];
  static budgetMs = 0.35;

  constructor() {
    super();
    this.bag = new DisposalBag('ui');
    this.root = null;
    this.hud = null;
    this.screen = null;
    this.combat = null;
    this.input = null;
    this.ctx = null;
    this.refs = {};
    this.roster = [];
    this.actions = [];
    this.focusIndex = 0;
    this.fighterIndex = 0;
    this.pendingMode = 'vs-ai';
    this.pauseReturn = 'fight';
    this.lastScreen = '';
    this.lastAnnounce = '';
    this.announceUntilTick = 0;
    this.pendingResultsTick = -1;
    this.lastResult = { winner: -2, rounds0: 0, rounds1: 0, perfectRounds: 0 };
    this.unsubs = [];
    this._padStartDown = false;
    this._navigatePayload = { tick: 0, screen: '', from: '' };
    this._cuePayload = {
      tick: 0, cue: '', x: 0, y: 1, z: 0, gain: 1, pitch: 1, variant: 0, surface: '',
    };
  }

  async init(ctx) {
    this.ctx = ctx;
    this.combat = ctx.systems.get('combat-sim');
    this.input = ctx.systems.get('core-input');
    this.root = document.getElementById('ui-root');
    if (!this.root) throw new Error('ui: #ui-root is missing.');

    const rosterView = await loadRosterView(ctx);
    this.roster = rosterView.fighters;
    const selected = this.roster.findIndex((fighter) => fighter.id === ctx.app.selected[0]);
    this.fighterIndex = selected >= 0 ? selected : 0;

    this.root.style.setProperty('--ui-scale', String(ctx.config.get('uiScale')));
    this.root.dataset.highContrast = ctx.config.get('highContrastFighters') ? '1' : '0';
    this.root.dataset.reduceFlash = ctx.config.get('reduceFlashing') ? '1' : '0';
    this.root.dataset.reduceRain = ctx.config.get('reduceRain') ? '1' : '0';
    this._buildHud();
    this._buildScreen();
    if (ctx.app.screen === 'boot') ctx.app.screen = 'title';

    if (MENU_SCREENS.has(ctx.app.screen) && ctx.app.screen !== 'results') {
      this.combat.pause(ctx);
      ctx.flags.paused = true;
    }

    this.unsubs.push(ctx.events.on('combat:round-start', (p) => {
      this._announce(`ROUND ${p.round}`, 'FIGHT', 'fight', 90, p.tick);
    }));
    this.unsubs.push(ctx.events.on('combat:round-end', (p) => {
      const winner = p.winner === 0 ? 'YOU WIN' : p.winner === 1 ? 'COMPUTER WINS' : 'DRAW';
      const sub = p.reason === 'timeout' ? 'TIME' : p.perfect ? 'PERFECT' : p.reason.toUpperCase();
      this._announce(
        winner,
        sub,
        p.winner === 0 ? 'p0' : p.winner === 1 ? 'p1' : 'time',
        260,
        p.tick,
      );
    }));
    this.unsubs.push(ctx.events.on('combat:match-end', (p) => {
      this.lastResult.winner = p.winner;
      this.lastResult.rounds0 = p.rounds0;
      this.lastResult.rounds1 = p.rounds1;
      this.lastResult.perfectRounds = p.perfectRounds;
      this.pendingResultsTick = p.tick + RESULT_DELAY_TICKS;
    }));
    this.unsubs.push(ctx.config.onChange(
      'uiScale',
      (v) => this.root.style.setProperty('--ui-scale', String(v)),
    ));
    this.unsubs.push(ctx.config.onChange(
      'highContrastFighters',
      (v) => { this.root.dataset.highContrast = v ? '1' : '0'; },
    ));
    this.unsubs.push(ctx.config.onChange(
      'reduceFlashing',
      (v) => { this.root.dataset.reduceFlash = v ? '1' : '0'; },
    ));
    this.unsubs.push(ctx.config.onChange(
      'reduceRain',
      (v) => { this.root.dataset.reduceRain = v ? '1' : '0'; },
    ));

    this.bag.listener(window, 'keydown', (event) => this._onKeyDown(event), { capture: true });
    this.bag.listener(this.screen, 'click', (event) => this._onClick(event));
    this._renderScreen(ctx.app.screen, ctx);
  }

  _buildHud() {
    const hud = document.createElement('section');
    hud.className = 'hud';
    hud.setAttribute('aria-label', 'Fight status');
    hud.innerHTML = `
      <div class="hud-top">
        ${sideMarkup(0, 'YOU')}
        <div class="hud-centre">
          <div class="hud-mode-mark"><b>ONE PLAYER</b><span>ROOFTOP NINE</span></div>
          <div class="hud-timer">60</div>
          <div class="hud-round">ROUND 1 · FIRST TO 2</div>
        </div>
        ${sideMarkup(1, 'CPU')}
      </div>
      <div class="hud-combo hud-combo--p0">
        <b class="hud-combo-n">0</b><span class="hud-combo-unit">HITS</span>
        <span class="hud-combo-dmg">0</span>
      </div>
      <div class="hud-combo hud-combo--p1">
        <b class="hud-combo-n">0</b><span class="hud-combo-unit">HITS</span>
        <span class="hud-combo-dmg">0</span>
      </div>
      <div class="hud-announce" data-visible="0" data-tone="fight">
        <div class="hud-announce-slab">
          <strong class="hud-announce-main"></strong>
          <span class="hud-announce-sub"></span>
        </div>
      </div>
      <div class="solo-controls">
        <span><b>MOVE</b> W A S D</span>
        <span><b>STRIKE</b> F G V B</span>
        <span><b>STANCE</b> SHIFT</span>
        <span><b>GUARD</b> SPACE</span>
        <span><b>PAUSE</b> ESC</span>
        <i>COMPUTER · <em>SKILLED</em></i>
      </div>
    `;
    this.root.appendChild(hud);
    this.hud = hud;
    this.bag.custom(() => hud.remove());

    for (let i = 0; i < 2; i++) {
      const side = hud.querySelector(`.hud-side--p${i}`);
      this.refs[`side${i}`] = side;
      this.refs[`name${i}`] = side.querySelector('.hud-name');
      this.refs[`arch${i}`] = side.querySelector('.hud-arch');
      this.refs[`fill${i}`] = side.querySelector('.hud-bar-fill');
      this.refs[`ghost${i}`] = side.querySelector('.hud-bar-ghost');
      this.refs[`hp${i}`] = side.querySelector('.hud-bar-hp');
      this.refs[`bar${i}`] = side.querySelector('.hud-bar');
      this.refs[`meterLabel${i}`] = side.querySelector('.hud-meter-label');
      this.refs[`meter${i}`] = side.querySelector('.hud-meter');
      this.refs[`meterValue${i}`] = side.querySelector('.hud-meter-val');
      this.refs[`meterSegs${i}`] = [...side.querySelectorAll('.hud-meter-segs i')];
      this.refs[`pips${i}`] = [...side.querySelectorAll('.hud-pips i')];
      const combo = hud.querySelector(`.hud-combo--p${i}`);
      this.refs[`combo${i}`] = combo;
      this.refs[`comboN${i}`] = combo.querySelector('.hud-combo-n');
      this.refs[`comboDmg${i}`] = combo.querySelector('.hud-combo-dmg');
    }
    this.refs.timer = hud.querySelector('.hud-timer');
    this.refs.round = hud.querySelector('.hud-round');
    this.refs.announce = hud.querySelector('.hud-announce');
    this.refs.announceMain = hud.querySelector('.hud-announce-main');
    this.refs.announceSub = hud.querySelector('.hud-announce-sub');
    this.refs.difficulty = hud.querySelector('.solo-controls em');
  }

  _buildScreen() {
    const screen = document.createElement('section');
    screen.className = 'screen screen--overlay flow-screen';
    screen.dataset.visible = '0';
    screen.dataset.state = 'active';
    screen.setAttribute('aria-label', 'Single-player menu');
    this.root.appendChild(screen);
    this.screen = screen;
    this.bag.custom(() => screen.remove());
  }

  update(dt, ctx) {
    const fighters = ctx.sim.fighters;
    const match = ctx.sim.match;
    const screenName = ctx.app.screen === 'boot' ? 'title' : ctx.app.screen;
    const fightVisible = screenName === 'fight' || screenName === 'training';
    this.hud.dataset.visible = fightVisible ? '1' : '0';
    this.screen.dataset.visible = fightVisible ? '0' : '1';
    this.hud.hidden = !fightVisible;
    this.screen.hidden = fightVisible;
    this.hud.setAttribute('aria-hidden', fightVisible ? 'false' : 'true');
    this.screen.setAttribute('aria-hidden', fightVisible ? 'true' : 'false');
    this.root.dataset.gameMode = screenName === 'training' ? 'training' : 'versus';

    if (screenName !== this.lastScreen) this._renderScreen(screenName, ctx);
    if (
      this.pendingResultsTick >= 0
      && screenName === 'fight'
      && ctx.time.tick >= this.pendingResultsTick
    ) {
      this.pendingResultsTick = -1;
      this._navigate('results', ctx);
    }

    this._pollGamepad(ctx);

    if (fighters.length === 2) {
      for (let i = 0; i < 2; i++) this._updateSide(i, fighters[i], match);
    }
    if (match) {
      setText(this.refs.timer, String(Math.max(0, Math.ceil(match.timerTicks / 120))).padStart(2, '0'));
      this.refs.timer.dataset.low = match.timerTicks <= 10 * 120 ? '1' : '0';
      this.refs.timer.classList.toggle('fx-blink', match.timerTicks <= 10 * 120);
      setText(this.refs.round, screenName === 'training'
        ? 'TRAINING · LIVE FRAME DATA'
        : `ROUND ${match.round} · FIRST TO ${Math.ceil(match.bestOf / 2)}`);
    }
    setText(
      this.refs.difficulty,
      screenName === 'training' ? 'TRAINING DUMMY' : String(ctx.app.aiDifficulty).toUpperCase(),
    );

    this.refs.announce.dataset.visible = ctx.time.tick < this.announceUntilTick ? '1' : '0';
    void dt;
  }

  _updateSide(i, fighter, match) {
    setText(this.refs[`name${i}`], fighter.stats.name);
    const role = i === 0 ? fighter.stats.archetype : `${fighter.stats.archetype} · AI`;
    setText(this.refs[`arch${i}`], role);
    const health = clamp(fighter.health / fighter.maxHealth, 0, 1);
    this.refs[`fill${i}`].style.setProperty('--v', health.toFixed(4));
    this.refs[`ghost${i}`].style.setProperty('--v', health.toFixed(4));
    setText(this.refs[`hp${i}`], `${Math.ceil(fighter.health)} / ${fighter.maxHealth}`);
    this.refs[`bar${i}`].dataset.danger = health <= 0.25 ? '1' : '0';
    this.refs[`bar${i}`].classList.toggle('fx-pulse', health <= 0.25);

    setText(this.refs[`meterLabel${i}`], fighter.stats.meterName);
    setText(this.refs[`meterValue${i}`], String(Math.floor(fighter.meter)));
    const meterFrac = clamp(fighter.meter / fighter.meterMax, 0, 1);
    const segs = this.refs[`meterSegs${i}`];
    for (let s = 0; s < segs.length; s++) {
      const on = meterFrac >= (s + 1) / segs.length;
      segs[s].dataset.on = on ? (meterFrac >= 1 ? '2' : '1') : '0';
    }
    this.refs[`meter${i}`].dataset.full = meterFrac >= 1 ? '1' : '0';
    const rounds = match ? match.rounds[i] : 0;
    const pips = this.refs[`pips${i}`];
    for (let p = 0; p < pips.length; p++) pips[p].dataset.on = p < rounds ? '1' : '0';

    const combo = this.refs[`combo${i}`];
    combo.dataset.visible = fighter.comboHits >= 2 ? '1' : '0';
    setText(this.refs[`comboN${i}`], String(fighter.comboHits));
    setText(this.refs[`comboDmg${i}`], String(fighter.comboDamage));
  }

  _renderScreen(name, ctx) {
    this.lastScreen = name;
    this.screen.dataset.screen = name;
    this.screen.setAttribute('aria-label', screenAriaLabel(name));
    if (name === 'fight' || name === 'training') {
      this.screen.innerHTML = '';
      this.actions = [];
      return;
    }

    if (name === 'select') this.screen.innerHTML = this._selectMarkup();
    else if (name === 'difficulty') this.screen.innerHTML = this._difficultyMarkup(ctx);
    else if (name === 'pause') this.screen.innerHTML = this._pauseMarkup(ctx);
    else if (name === 'results') this.screen.innerHTML = this._resultsMarkup(ctx);
    else this.screen.innerHTML = this._titleMarkup();

    this.actions = [...this.screen.querySelectorAll('[data-ui-action]')];
    if (name === 'select') {
      const id = this.roster[this.fighterIndex]?.id;
      this.focusIndex = Math.max(0, this.actions.findIndex((el) => el.dataset.value === id));
    } else if (name === 'difficulty') {
      this.focusIndex = difficultyIndex(ctx.app.aiDifficulty);
    } else {
      this.focusIndex = 0;
    }
    this._applyFocus(false);
  }

  _titleMarkup() {
    return `
      <div class="flow-vignette"></div>
      <div class="title-rain" aria-hidden="true">${rainMarkup()}</div>
      <div class="screen-shell flow-title-shell">
        <main class="title-stage">
          <div class="title-core">
            <div class="flow-logo-mark" aria-hidden="true"><i></i><b>IX</b><i></i></div>
            <h1 class="flow-wordmark"><span>CLAUDE</span><strong>OF IRON</strong></h1>
            <div class="title-strap">
              <i class="title-strap-dash"></i>
              An original one-player fighting game
            </div>
            <div class="menu-list title-menu" role="menu">
              ${menuItem('begin', '', 'Begin Operation', 'Fight the computer', true, true)}
              ${menuItem('training', '', 'Training Lab', 'Learn one fighter', false, false)}
            </div>
          </div>
          <footer class="title-meta">
            <span>ROOFTOP NINE // 02:17</span>
            <span>ONE HUMAN</span>
            <span>ONE ADAPTIVE MACHINE</span>
          </footer>
        </main>
      </div>
    `;
  }

  _selectMarkup() {
    const human = this.roster[this.fighterIndex] || this.roster[0];
    const cpu = computerOpponent(this.roster, human?.id);
    const training = this.pendingMode === 'training';
    return `
      <div class="flow-vignette"></div>
      <div class="screen-shell">
        ${screenHead(
          '01 // Choose Your Fighter',
          'One set of hands',
          training ? 'Computer dummy locked to slot 2' : 'Computer locked to slot 2',
        )}
        <div class="select-body">
          <section class="side-col" data-active="1">
            ${sideHead('YOU', 'Human controlled', 'Choose', 'var(--p0-500)')}
            ${fighterCard(human, 'p0')}
          </section>
          <section class="roster-col">
            <div class="panel-head">Available fighters</div>
            <div class="roster" role="menu">
              ${this.roster.map((fighter) => fighterTile(
                fighter,
                fighter.id === human?.id,
                fighter.id === cpu?.id,
              )).join('')}
            </div>
            <div class="notice">
              ${training
                ? 'Select your fighter. The other slot becomes a stationary computer training dummy.'
                : `Select only your fighter. The computer automatically takes the opposing style,
                  so every route remains a focused one-player duel.`}
            </div>
          </section>
          <section class="side-col" data-locked="1">
            ${sideHead(
              'CPU',
              training ? 'Computer training dummy' : 'Computer controlled',
              'Auto selected',
              'var(--p1-500)',
            )}
            ${fighterCard(cpu, 'p1')}
          </section>
        </div>
        ${screenFoot(
          '← / → or A / D',
          'Choose',
          'Enter / F',
          training ? 'Enter Training' : 'Confirm',
          'Esc',
          'Back',
        )}
      </div>
    `;
  }

  _difficultyMarkup(ctx) {
    const human = this.roster[this.fighterIndex] || this.roster[0];
    const cpu = computerOpponent(this.roster, human?.id);
    const current = difficultyIndex(ctx.app.aiDifficulty);
    return `
      <div class="flow-vignette"></div>
      <div class="screen-shell flow-difficulty-shell">
        ${screenHead('02 // Set Computer Protocol', 'Reaction and adaptation', 'No hidden inputs')}
        <div class="flow-difficulty-layout">
          <section class="flow-versus-panel">
            <span class="screen-eyebrow">Confirmed matchup</span>
            <div class="flow-versus-fighters">
              ${compactFighter(human, 'YOU', 'p0')}
              <b>VS</b>
              ${compactFighter(cpu, 'COMPUTER', 'p1')}
            </div>
            <p>
              The computer uses the same input and move rules you do. Difficulty changes reaction
              time, decision quality, defence, and deliberate mistakes.
            </p>
          </section>
          <section class="flow-difficulty-menu">
            <div class="panel-head">Computer difficulty</div>
            <div class="menu-list" role="menu">
              ${AI_DIFFICULTY_OPTIONS.map((option, index) => menuItem(
                'difficulty',
                option.id,
                `${String(index + 1).padStart(2, '0')} // ${option.label}`,
                option.note,
                index === current,
                option.id === 'skilled',
              )).join('')}
            </div>
            <p class="flow-difficulty-detail">
              ${AI_DIFFICULTY_OPTIONS[current].detail}
            </p>
          </section>
        </div>
        ${screenFoot('↑ / ↓ or W / S', 'Choose', 'Enter / F', 'Fight', 'Esc', 'Back')}
      </div>
    `;
  }

  _pauseMarkup(ctx) {
    const training = this.pauseReturn === 'training';
    return `
      <div class="pause-card" role="dialog" aria-modal="true" aria-label="Paused">
        <div class="pause-head">
          <h1 class="pause-title">${training ? 'TRAINING PAUSED' : 'FIGHT PAUSED'}</h1>
          <span class="pause-meta">ONE PLAYER<br>${escapeHtml(ctx.app.aiDifficulty).toUpperCase()} CPU</span>
        </div>
        <div class="menu-list" role="menu">
          ${menuItem('resume', '', 'Resume', 'Return to the rooftop', true, true)}
          ${menuItem('restart', '', training ? 'Reset Training' : 'Restart Match', 'Fresh health and positions')}
          ${menuItem('select', '', 'Change Fighter', 'Return to human fighter select')}
          ${menuItem('title', '', 'Return to Title', 'End this run', false, false, true)}
        </div>
        ${screenFoot('↑ / ↓', 'Choose', 'Enter / F', 'Confirm', 'Esc', 'Resume')}
      </div>
    `;
  }

  _resultsMarkup(ctx) {
    const match = ctx.sim.match;
    const [fighter0, fighter1] = ctx.sim.fighters;
    let winner = this.lastResult.winner;
    if (winner < -1) {
      if (match.rounds[0] !== match.rounds[1]) {
        winner = match.rounds[0] > match.rounds[1] ? 0 : 1;
      } else if (fighter0.health <= 0 || fighter1.health <= 0) {
        winner = fighter0.health > fighter1.health ? 0 : fighter1.health > fighter0.health ? 1 : -1;
      } else {
        winner = -1;
      }
    }
    const human = findFighter(this.roster, ctx.app.selected[0]);
    const cpu = findFighter(this.roster, ctx.app.selected[1]);
    const humanWon = winner === 0;
    const draw = winner < 0;
    const title = draw ? 'IRON DRAW' : humanWon ? 'ROOFTOP CLAIMED' : 'RUN ENDED';
    const outcome = draw ? 'No fighter yielded' : humanWon ? 'You defeated the computer' : 'Computer victory';
    const rounds0 = this.lastResult.rounds0 || match.rounds[0];
    const rounds1 = this.lastResult.rounds1 || match.rounds[1];
    return `
      <div class="flow-vignette"></div>
      <div class="screen-shell flow-results-shell">
        ${screenHead('Match Complete', 'Combat record', `Computer // ${ctx.app.aiDifficulty}`)}
        <div class="results-hero" style="--hero-color:${humanWon ? 'var(--p0-500)' : 'var(--p1-500)'}">
          <div class="results-hero-text">
            <span class="results-outcome">${outcome}</span>
            <h1 class="results-winner">${title}</h1>
          </div>
          <div class="results-flags">
            ${this.lastResult.perfectRounds > 0
              ? `<span class="flag-perfect">${this.lastResult.perfectRounds} PERFECT</span>`
              : ''}
          </div>
        </div>
        <div class="flow-results-body">
          <section class="panel">
            <div class="panel-head">Run summary</div>
            <div class="panel-body results-grid">
              ${resultsRow(
                human?.name || 'YOU',
                'FIGHTER',
                cpu?.name || 'COMPUTER',
                'results-row--head results-cell--name',
              )}
              ${resultsRow(String(rounds0), 'ROUNDS', String(rounds1))}
              ${resultsRow(
                String(match.bestCombo[0]),
                'BEST COMBO',
                String(match.bestCombo[1]),
              )}
              ${resultsRow(
                String(match.bestComboDamage[0]),
                'COMBO DAMAGE',
                String(match.bestComboDamage[1]),
              )}
              ${resultsRow(
                `${Math.max(0, Math.ceil(match.timerTicks / 120))}s`,
                'TIME LEFT',
                escapeHtml(ctx.app.aiDifficulty).toUpperCase(),
              )}
            </div>
          </section>
          <section class="menu-list flow-results-actions" role="menu">
            ${menuItem('rematch', '', 'Rematch', 'Same fighter · same computer', true, true)}
            ${menuItem('select', '', 'Change Fighter', 'Build a new matchup')}
            ${menuItem('title', '', 'Return to Title', 'Close this run', false, false, true)}
          </section>
        </div>
        ${screenFoot('↑ / ↓', 'Choose', 'Enter / F', 'Confirm', 'Esc', 'Title')}
      </div>
    `;
  }

  _onKeyDown(event) {
    if (event.repeat || !this.ctx) return;
    const screen = this.ctx.app.screen;
    if (screen === 'fight' || screen === 'training') {
      if (event.code === 'Escape') {
        event.preventDefault();
        this._pause(this.ctx);
      }
      return;
    }

    if (screen === 'pause' && event.code === 'Escape') {
      event.preventDefault();
      this._resume(this.ctx);
      return;
    }

    const up = event.code === 'ArrowUp' || event.code === 'KeyW';
    const down = event.code === 'ArrowDown' || event.code === 'KeyS';
    const left = event.code === 'ArrowLeft' || event.code === 'KeyA';
    const right = event.code === 'ArrowRight' || event.code === 'KeyD';
    const confirm = event.code === 'Enter' || event.code === 'Space' || event.code === 'KeyF';
    const cancel = event.code === 'Escape' || event.code === 'Backspace';
    if (!(up || down || left || right || confirm || cancel)) return;
    event.preventDefault();

    if (screen === 'select' && (up || down || left || right)) {
      this._cycleFighter(left || up ? -1 : 1);
    } else if (up || left) {
      this._moveFocus(-1);
    } else if (down || right) {
      this._moveFocus(1);
    } else if (confirm) {
      this._confirmFocused();
    } else if (cancel) {
      this._cancel(this.ctx);
    }
  }

  _onClick(event) {
    const target = event.target.closest('[data-ui-action]');
    if (!target || !this.screen.contains(target)) return;
    const index = this.actions.indexOf(target);
    if (index >= 0) {
      this.focusIndex = index;
      this._applyFocus(false);
    }
    this._activate(target.dataset.uiAction, target.dataset.value || '', this.ctx);
  }

  _pollGamepad(ctx) {
    const screen = ctx.app.screen;
    const snap = ctx.input.players[0];
    if (snap && snap.device === 'gamepad' && MENU_SCREENS.has(screen)) {
      if (screen === 'select' && (snap.pressedLeft || snap.pressedUp)) this._cycleFighter(-1);
      else if (screen === 'select' && (snap.pressedRight || snap.pressedDown)) this._cycleFighter(1);
      else if (snap.pressedUp || snap.pressedLeft) this._moveFocus(-1);
      else if (snap.pressedDown || snap.pressedRight) this._moveFocus(1);
      else if (snap.pressedLp || snap.pressedLk) this._confirmFocused();
      else if (snap.pressedHp || snap.pressedHk) this._cancel(ctx);
    }

    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    const pad = pads && pads[0];
    const startDown = !!(pad && pad.buttons && pad.buttons[9] && pad.buttons[9].pressed);
    if (startDown && !this._padStartDown) {
      if (screen === 'fight' || screen === 'training') this._pause(ctx);
      else if (screen === 'pause') this._resume(ctx);
    }
    this._padStartDown = startDown;
  }

  _cycleFighter(delta) {
    if (this.roster.length === 0) return;
    this.fighterIndex = (this.fighterIndex + delta + this.roster.length) % this.roster.length;
    this._uiCue('ui-move');
    this._renderScreen('select', this.ctx);
  }

  _moveFocus(delta) {
    if (this.actions.length === 0) return;
    this.focusIndex = (this.focusIndex + delta + this.actions.length) % this.actions.length;
    if (this.ctx.app.screen === 'difficulty') {
      const action = this.actions[this.focusIndex];
      const option = difficultyIndex(action.dataset.value);
      const detail = this.screen.querySelector('.flow-difficulty-detail');
      setText(detail, AI_DIFFICULTY_OPTIONS[option].detail);
    }
    this._applyFocus(true);
    this._uiCue('ui-move');
  }

  _applyFocus(moveDomFocus) {
    for (let i = 0; i < this.actions.length; i++) {
      this.actions[i].dataset.focused = i === this.focusIndex ? '1' : '0';
      this.actions[i].tabIndex = i === this.focusIndex ? 0 : -1;
    }
    const focused = this.actions[this.focusIndex];
    if (moveDomFocus && focused) focused.focus({ preventScroll: true });
  }

  _confirmFocused() {
    const action = this.actions[this.focusIndex];
    if (action) this._activate(action.dataset.uiAction, action.dataset.value || '', this.ctx);
  }

  _activate(action, value, ctx) {
    if (!ctx) return;
    if (action === 'begin') {
      this.pendingMode = 'vs-ai';
      this._navigate('select', ctx);
    } else if (action === 'training') {
      this.pendingMode = 'training';
      this._navigate('select', ctx);
    } else if (action === 'fighter') {
      const index = this.roster.findIndex((fighter) => fighter.id === value);
      if (index >= 0) this.fighterIndex = index;
      if (this.pendingMode === 'training') this._startGame(ctx, true);
      else this._navigate('difficulty', ctx);
    } else if (action === 'difficulty') {
      this._startGame(ctx, false, value);
    } else if (action === 'resume') {
      this._resume(ctx);
    } else if (action === 'restart') {
      this._startGame(ctx, this.pauseReturn === 'training');
    } else if (action === 'select') {
      this.pendingMode = ctx.app.screen === 'pause' && this.pauseReturn === 'training'
        ? 'training'
        : 'vs-ai';
      this._navigate('select', ctx);
    } else if (action === 'rematch') {
      this.combat.setTrainingMode(ctx, false);
      this.combat.rematch(ctx);
      this.input.resetAll();
      this._enterCombatScreen('fight', ctx);
    } else if (action === 'title') {
      this._navigate('title', ctx);
    }
    this._uiCue('ui-confirm');
  }

  _cancel(ctx) {
    const screen = ctx.app.screen;
    if (screen === 'select') this._navigate('title', ctx);
    else if (screen === 'difficulty') this._navigate('select', ctx);
    else if (screen === 'pause') this._resume(ctx);
    else if (screen === 'results') this._navigate('title', ctx);
    else return;
    this._uiCue('ui-cancel');
  }

  _startGame(ctx, training, difficulty = ctx.app.aiDifficulty) {
    const human = this.roster[this.fighterIndex] || this.roster[0];
    const opponent = computerOpponent(this.roster, human?.id);
    if (!human || !opponent) return;

    this.combat.setTrainingMode(ctx, false);
    ctx.app.mode = training ? 'training' : 'vs-ai';
    ctx.app.aiDifficulty = difficulty || 'skilled';
    ctx.config.set('aiDifficulty', ctx.app.aiDifficulty);
    ctx.app.selected = [human.id, opponent.id];
    this.combat.setFighters(ctx, human.id, opponent.id);
    if (training) {
      this.combat.setTrainingMode(ctx, true);
      this.combat.resetPositions(ctx, 'neutral');
      this._announce('TRAINING', 'READY', 'fight', 100, ctx.time.tick);
    }
    this.input.resetAll();
    this.pendingResultsTick = -1;
    this._enterCombatScreen(training ? 'training' : 'fight', ctx);
  }

  _pause(ctx) {
    if (ctx.app.screen !== 'fight' && ctx.app.screen !== 'training') return;
    this.pauseReturn = ctx.app.screen;
    this.combat.pause(ctx);
    ctx.flags.paused = true;
    this._setScreen('pause', ctx);
    this._uiCue('ui-confirm');
  }

  _resume(ctx) {
    this.combat.resume(ctx);
    ctx.flags.paused = false;
    this._setScreen(this.pauseReturn, ctx);
    this._uiCue('ui-cancel');
  }

  _navigate(name, ctx) {
    const from = ctx.app.screen;
    if (name === 'title' || name === 'select' || name === 'difficulty') {
      this.combat.pause(ctx);
      this.combat.setTrainingMode(ctx, false);
      ctx.flags.paused = true;
      this.input.resetAll();
    }
    if (name === 'results') {
      this.combat.pause(ctx);
      ctx.flags.paused = true;
    }
    this._setScreen(name, ctx, from);
  }

  _enterCombatScreen(name, ctx) {
    ctx.flags.paused = false;
    this._setScreen(name, ctx);
  }

  _setScreen(name, ctx, explicitFrom = ctx.app.screen) {
    if (ctx.app.screen === name && this.lastScreen === name) return;
    ctx.app.screen = name;
    const payload = this._navigatePayload;
    payload.tick = ctx.time.tick;
    payload.screen = name;
    payload.from = explicitFrom;
    ctx.events.emit('ui:navigate', payload);
    this._renderScreen(name, ctx);
  }

  _uiCue(cue) {
    if (!this.ctx) return;
    const payload = this._cuePayload;
    payload.tick = this.ctx.time.tick;
    payload.cue = cue;
    this.ctx.events.emit('audio:cue', payload);
  }

  _announce(main, sub, tone, ticks, currentTick) {
    setText(this.refs.announceMain, main);
    setText(this.refs.announceSub, sub);
    this.refs.announce.dataset.tone = tone;
    this.announceUntilTick = currentTick + ticks;
    this.lastAnnounce = main;
  }

  state() {
    return {
      screen: this.ctx ? this.ctx.app.screen : '',
      pendingMode: this.pendingMode,
      human: this.roster[this.fighterIndex]?.id || '',
      computer: computerOpponent(this.roster, this.roster[this.fighterIndex]?.id)?.id || '',
      difficulty: this.ctx ? this.ctx.app.aiDifficulty : '',
      focusIndex: this.focusIndex,
      actions: this.actions.map((action) => action.dataset.uiAction),
    };
  }

  dispose() {
    for (const off of this.unsubs) off();
    this.unsubs.length = 0;
    this.bag.disposeAll();
    this.actions.length = 0;
    this.roster.length = 0;
    this.refs = {};
    this.root = null;
    this.hud = null;
    this.screen = null;
    this.combat = null;
    this.input = null;
    this.ctx = null;
  }
}

function sideMarkup(index, slot) {
  return `
    <div class="hud-side hud-side--p${index}">
      <div class="hud-idplate">
        <span class="hud-slot">${slot}</span>
        <strong class="hud-name">${index === 0 ? 'KESTREL' : 'BULWARK'}</strong>
        <span class="hud-arch">${index === 0 ? 'COUNTER-STRIKER' : 'POWER GRAPPLER · AI'}</span>
      </div>
      <div class="hud-bar">
        <div class="hud-bar-track">
          <i class="hud-bar-ghost" style="--v:1"></i>
          <i class="hud-bar-fill" style="--v:1"></i>
        </div>
        <i class="hud-bar-grid"></i>
        <span class="hud-bar-hp">0 / 0</span>
      </div>
      <div class="hud-underrow">
        <div class="hud-pips"><i></i><i></i></div>
        <div class="hud-meter">
          <b class="hud-meter-label">METER</b>
          <span class="hud-meter-segs">${'<i></i>'.repeat(10)}</span>
          <span class="hud-meter-val">0</span>
        </div>
      </div>
    </div>
  `;
}

function menuItem(
  action,
  value,
  label,
  note,
  focused = false,
  primary = false,
  danger = false,
) {
  const classes = [
    'menu-item',
    primary ? 'menu-item--primary' : '',
    danger ? 'menu-item--danger' : '',
  ].filter(Boolean).join(' ');
  return `
    <button type="button" class="${classes}" role="menuitem"
      data-ui-action="${action}" data-value="${escapeHtml(value)}"
      data-focused="${focused ? '1' : '0'}">
      <i class="menu-item-tick"></i>
      <span class="menu-item-label">${escapeHtml(label)}</span>
      <span class="menu-item-note">${escapeHtml(note)}</span>
    </button>
  `;
}

function screenHead(eyebrow, title, aside) {
  return `
    <header class="screen-head">
      <div class="screen-head-titles">
        <span class="screen-eyebrow">${eyebrow}</span>
        <h1 class="screen-title">${title}</h1>
      </div>
      <div class="screen-head-aside">
        <span class="badge badge--p0">YOU</span>
        <span class="badge badge--ghost">${aside}</span>
        <span class="badge badge--p1">CPU</span>
      </div>
    </header>
  `;
}

function sideHead(slot, role, state, color) {
  return `
    <header class="side-head" style="--side-color:${color}">
      <span class="side-head-slot">${slot}</span>
      <span class="side-head-role">${role}</span>
      <span class="side-head-state">${state}</span>
    </header>
  `;
}

function fighterCard(fighter, side) {
  if (!fighter) return '<div class="card"></div>';
  const mechanics = fighter.mechanic.slice(0, 2);
  return `
    <article class="card flow-fighter-card" style="--side-color:var(--${side}-500)">
      <div class="card-art ${side === 'p1' ? 'card-art--p1' : ''}">
        ${fighterGlyph(fighter)}
        <i class="card-art-floor"></i>
      </div>
      <div class="card-body">
        <h2 class="card-name">${escapeHtml(fighter.name)}</h2>
        <span class="card-arch">${escapeHtml(fighter.archetype)}</span>
        <p class="card-tag">“${escapeHtml(fighter.tagline)}”</p>
        <div class="card-block">
          <span class="card-block-title">Execution load</span>
          <div class="diff">
            <span class="diff-pips">${pipMarkup(fighter.difficulty, 5)}</span>
            <span class="diff-word">${escapeHtml(fighter.difficultyWord)}</span>
          </div>
        </div>
        <div class="card-block">
          <span class="card-block-title">Combat identity</span>
          ${mechanics.map((line) => `<p class="card-mech">${escapeHtml(line)}</p>`).join('')}
        </div>
      </div>
    </article>
  `;
}

function fighterTile(fighter, human, computer) {
  return `
    <button type="button" class="tile" role="menuitem" data-ui-action="fighter"
      data-value="${escapeHtml(fighter.id)}" data-focused="${human ? '1' : '0'}">
      <span class="tile-glyph">${fighterGlyph(fighter, true)}</span>
      <span class="tile-flags">
        <i class="tile-flag tile-flag--p0" data-on="${human ? '1' : '0'}">YOU</i>
        <i class="tile-flag tile-flag--p1" data-on="${computer ? '1' : '0'}">CPU</i>
      </span>
      <strong class="tile-name">${escapeHtml(fighter.name)}</strong>
      <span class="tile-arch">${escapeHtml(fighter.archetype)}</span>
    </button>
  `;
}

function compactFighter(fighter, role, side) {
  if (!fighter) return '';
  return `
    <article class="flow-compact-fighter flow-compact-fighter--${side}">
      <span>${role}</span>
      ${fighterGlyph(fighter, true)}
      <strong>${escapeHtml(fighter.name)}</strong>
      <small>${escapeHtml(fighter.archetype)}</small>
    </article>
  `;
}

function fighterGlyph(fighter, compact = false) {
  const bulwark = fighter.id === 'bulwark';
  const body = bulwark
    ? `
      <path class="silhouette-accent silhouette-accent--dim"
        d="M43 4L51 0 60 4 58 9 45 9Z"/>
      <circle class="silhouette-skin" cx="51" cy="14" r="11"/>
      <path class="silhouette-detail" d="M42 9Q51 2 61 9L59 13 42 12Z"/>
      <path class="silhouette-skin" d="M45 23H58L61 32H42Z"/>
      <path class="silhouette-gear" d="M36 27L27 36 25 67 34 73 38 65 65 65 70 74
        79 66 76 35 66 27 60 24 43 24Z"/>
      <path class="silhouette-plate" d="M38 28L28 37 31 49 40 43 64 43 73 50 77 37
        66 28 59 35 44 35Z"/>
      <path class="silhouette-detail" d="M39 43H65L69 67 62 76H42L34 66Z"/>
      <path class="silhouette-accent" d="M39 47H65V51H39ZM38 65H68V69H38Z"/>
      <path class="silhouette-skin" d="M25 39L17 49 15 66 22 70 30 59 34 40Z"/>
      <path class="silhouette-skin" d="M77 39L85 48 88 63 81 68 72 58 69 40Z"/>
      <path class="silhouette-plate" d="M15 55L27 54 31 64 22 73 13 67Z"/>
      <path class="silhouette-plate" d="M76 53L88 54 91 64 82 72 73 63Z"/>
      <path class="silhouette-accent" d="M16 57L29 57V60H15ZM18 63L29 62V65L17 66Z"/>
      <path class="silhouette-accent" d="M75 56L88 57 89 60 76 59ZM76 62L89 63 88 66 75 65Z"/>
      <path class="silhouette-gear" d="M39 74L51 72 63 75 67 103 59 111 51 84
        45 111 35 106Z"/>
      <path class="silhouette-plate" d="M35 100L47 99 46 114H31L29 109ZM58 99L69 100
        74 109 72 114H57Z"/>
      <path class="silhouette-accent" d="M34 101H47V104H33ZM58 101H70L71 104H58Z"/>
      <path class="silhouette-edge" d="M36 27L27 36 25 67 34 73 39 74 35 106
        31 114H46L51 84 57 114H72L67 103 63 75 70 74 79 66 76 35 66 27
        M38 43H65M39 74H63"/>
    `
    : `
      <path class="silhouette-accent silhouette-accent--dim" d="M57 21L87 29 61 34Z"/>
      <circle class="silhouette-skin" cx="51" cy="14" r="9"/>
      <path class="silhouette-detail" d="M42 12Q47 3 59 8L62 13 56 12 54 7 45 13Z"/>
      <path class="silhouette-skin" d="M47 21H56L58 30H44Z"/>
      <path class="silhouette-gear" d="M41 25L32 34 34 55 38 72 63 72 67 54 69 32
        60 25 55 31 46 31Z"/>
      <path class="silhouette-plate" d="M41 27L34 36 39 48 46 40 59 40 66 48
        68 35 60 27 56 33 46 33Z"/>
      <path class="silhouette-accent" d="M39 47H65V51H39ZM39 66H63V69H39Z"/>
      <path class="silhouette-skin" d="M34 34L25 42 18 58 23 63 33 54 41 40Z"/>
      <path class="silhouette-skin" d="M68 33L76 39 84 53 79 59 68 52 61 39Z"/>
      <path class="silhouette-plate" d="M18 52L28 54 31 63 24 70 16 64Z"/>
      <path class="silhouette-plate" d="M75 49L84 51 89 59 82 67 75 61Z"/>
      <path class="silhouette-accent" d="M18 55L29 57 28 60 17 58ZM76 52L86 55
        87 58 76 56Z"/>
      <path class="silhouette-gear" d="M39 72L50 70 62 72 64 85 73 108 65 113
        54 88 48 88 41 112 31 109 37 84Z"/>
      <path class="silhouette-plate" d="M31 103L43 102 43 114H27L26 109ZM62 102
        73 104 78 110 76 114H61Z"/>
      <path class="silhouette-accent" d="M30 104H43V107H29ZM62 104H74L76 107H63Z"/>
      <path class="silhouette-edge" d="M41 25L32 34 25 42 18 58 23 63 33 54 38 72
        37 84 31 109 27 114H43L48 88 54 88 61 114H76L73 108 64 85 62 72
        68 52 79 59 84 53 76 39 69 32 60 25M39 47H65M39 72H62"/>
    `;
  return `
    <svg class="silhouette silhouette--${escapeHtml(fighter.id)}
      ${compact ? 'silhouette--compact' : ''}" viewBox="0 0 104 116"
      role="img" aria-label="${escapeHtml(fighter.name)} fighter silhouette">
      ${body}
    </svg>
  `;
}

function screenFoot(k1, v1, k2, v2, k3, v3) {
  return `
    <footer class="screen-foot">
      <div class="hintbar">
        <span class="hint"><i class="key">${k1}</i>${v1}</span>
        <span class="hint"><i class="key">${k2}</i>${v2}</span>
        <span class="hint"><i class="key">${k3}</i>${v3}</span>
      </div>
      <span class="u-spacer"></span>
      <span class="u-eyebrow">ONE PLAYER // COMPUTER OPPONENT</span>
    </footer>
  `;
}

function resultsRow(left, metric, right, extra = '') {
  return `
    <div class="results-row ${extra}">
      <span class="results-cell results-cell--p0">${escapeHtml(left)}</span>
      <span class="results-metric">${escapeHtml(metric)}</span>
      <span class="results-cell results-cell--p1">${escapeHtml(right)}</span>
    </div>
  `;
}

function pipMarkup(on, total) {
  let out = '';
  for (let i = 0; i < total; i++) out += `<i data-on="${i < on ? '1' : '0'}"></i>`;
  return out;
}

function rainMarkup() {
  let out = '';
  for (let i = 0; i < 20; i++) {
    const x = 3 + i * 5;
    const scale = 0.55 + (i % 5) * 0.16;
    const duration = 1.5 + (i % 4) * 0.24;
    const offset = -((i * 0.37) % 2.4);
    out += `<i style="--x:${x}%;--s:${scale};--d:${duration}s;--o:${offset}s"></i>`;
  }
  return out;
}

function screenAriaLabel(name) {
  const labels = {
    title: 'Claude of Iron title',
    select: 'Choose your fighter',
    difficulty: 'Choose computer difficulty',
    pause: 'Fight paused',
    results: 'Match results',
  };
  return labels[name] || 'Single-player fight';
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function setText(el, value) {
  if (el && el.textContent !== value) el.textContent = value;
}

export { AI_DIFFICULTY_OPTIONS, computerOpponent };
